import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useState } from 'react'

/**
 * Re-read one resource without letting a failed recovery poison its standing query.
 *
 * Conflict recovery must call the transport directly: `fetchQuery` stores a
 * rejection under the query the open editor is rendered from, which can unmount
 * the form and lose the very edits recovery is meant to preserve. A successful
 * read is admitted through the resource owner's cache writer; a failure returns
 * `null` and changes no shared state.
 */
export function useResourceReload<TArgs extends unknown[], TResult>(
  read: (...args: TArgs) => Promise<TResult>,
  accept: (queryClient: QueryClient, value: TResult) => void,
): (...args: TArgs) => Promise<TResult | null> {
  const queryClient = useQueryClient()
  return async (...args: TArgs): Promise<TResult | null> => {
    try {
      const value = await read(...args)
      accept(queryClient, value)
      return value
    } catch {
      return null
    }
  }
}

/**
 * What the last reload attempt said. `null` is "not asked yet", which is not
 * the same as either answer and is why this is not a boolean.
 */
export type ConflictReloadNotice = 'reloaded' | 'failed'

export interface ReloadNotice {
  /** The outcome of the LAST attempt, or null while none has been made. */
  readonly notice: ConflictReloadNotice | null
  /** Re-read; on success drop the refusal, so the form is armed again. */
  readonly reload: () => Promise<void>
  /** Forget the notice. Called where a command succeeds. */
  readonly clear: () => void
}

export interface ConflictRecovery extends ReloadNotice {
  /** The refusal on screen is a stale-revision one, so a reload can rebase it. */
  readonly stale: boolean
}

interface ReloadInput {
  /** Re-read the resource; `null` when the read itself failed. */
  read: () => Promise<unknown | null>
  /** Clear the refused command(s), so the editor can be submitted again. */
  reset: () => void
}

/**
 * WHAT A RE-READ LEAVES BEHIND, and in which order.
 *
 * THE SECOND FAILURE IS DECIDED HERE, ONCE: the notice is the answer to the
 * LAST attempt, so a reload that fails after one that worked falls to `failed`
 * and a retry that works rises back. The alternative — a notice that sticks at
 * its best answer — tells someone whose connection has since dropped that the
 * form under their hands is fresh, which is the one thing the notice exists to
 * be right about.
 *
 * The refusal is only dropped when the read SUCCEEDED. A reset on a failed
 * reload would take the refusal off the screen and leave the draft sitting on
 * the revision that was already refused, with nothing left saying so.
 *
 * WHAT THE FRESH VALUE DOES to the form is the caller's, inside `read`: only
 * the caller knows which pieces of its own state a re-read replaces. What is
 * never the caller's is the answer above.
 *
 * Held apart from the recovery below because an editor can need this ordering
 * without holding the staleness predicate: the shared record editor
 * (`shared/persons/recordEditor.ts`) drives the re-read while the page around
 * it decides whether the refusal on screen is the rebaseable one.
 */
export function useReloadNotice({ read, reset }: ReloadInput): ReloadNotice {
  const [notice, setNotice] = useState<ConflictReloadNotice | null>(null)
  return {
    notice,
    reload: async () => {
      if ((await read()) === null) {
        setNotice('failed')
        return
      }
      reset()
      setNotice('reloaded')
    },
    clear: () => setNotice(null),
  }
}

/**
 * THE RECOVERY AROUND A REFUSED WRITE, in one place.
 *
 * Every editor that saves against a revision needs the same five pieces: the
 * direct read this file opens with, a predicate for "this refusal is a stale
 * revision", a notice with three states, a mutation reset, and a pair of
 * message ids. Three editors had assembled them by hand — the roles panel, the
 * user's roles form and the connector panel — and the pieces are not
 * independent: the reset has to happen only on a SUCCESSFUL read, and the
 * notice has to be cleared by the next successful save or it contradicts what
 * is on screen. Each copy decided that ordering again, and the fourth editor
 * would have decided it a fourth time. Nothing about it is per-editor except
 * the words.
 *
 * The predicate rides along rather than being read from a second place at the
 * render site, so one object answers both questions a refusal raises: whether a
 * reload can rebase it, and what the last reload said.
 */
export function useConflictRecovery({
  stale,
  ...input
}: ReloadInput & {
  /** Whether the refusal currently on screen is the stale-revision one. */
  stale: boolean
}): ConflictRecovery {
  const notice = useReloadNotice(input)
  return { stale, ...notice }
}
